import { useState } from "react";
import { MEALS, RSVP_EVENTS } from "../../data/content";
import { submitRsvp } from "../../lib/guests";
import type { Attending, Invitation, MemberResponse, PlusOne } from "../../lib/types";

interface Props {
  invitation: Invitation;
  onSubmitted: (reply: Invitation) => void;
  onStartOver: () => void;
}

const EMPTY_PLUS_ONE: PlusOne = { name: "", attending: null, meal: null };

function AttendanceChips({
  value,
  onChange,
  label,
}: {
  value: Attending;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <div className="chip-row" role="group" aria-label={label}>
      <button
        type="button"
        className={`chip${value === true ? " chip--on" : ""}`}
        aria-pressed={value === true}
        onClick={() => onChange(true)}
      >
        Joyfully accepts
      </button>
      <button
        type="button"
        className={`chip${value === false ? " chip--on" : ""}`}
        aria-pressed={value === false}
        onClick={() => onChange(false)}
      >
        Regretfully declines
      </button>
    </div>
  );
}

export function RsvpForm({ invitation, onSubmitted, onStartOver }: Props) {
  const [responses, setResponses] = useState<MemberResponse[]>(invitation.responses);
  const [plusOne, setPlusOne] = useState<PlusOne>(invitation.plusOne ?? EMPTY_PLUS_ONE);
  const [events, setEvents] = useState<Record<string, boolean>>(
    Object.keys(invitation.events).length > 0 ? invitation.events : RSVP_EVENTS,
  );
  const [email, setEmail] = useState(invitation.email);
  const [note, setNote] = useState(invitation.note);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const anyoneAttending =
    responses.some((r) => r.attending === true) || plusOne.attending === true;

  function updateMember(index: number, patch: Partial<MemberResponse>) {
    setResponses((prev) =>
      prev.map((response, i) => (i === index ? { ...response, ...patch } : response)),
    );
  }

  function validate(): string {
    for (const [index, response] of responses.entries()) {
      if (response.attending === null) {
        return `Please let us know whether ${invitation.memberNames[index]} can make it.`;
      }
      if (response.attending && !response.meal) {
        return `Please choose an entrée for ${invitation.memberNames[index]}.`;
      }
    }

    if (invitation.plusOneAllowed && plusOne.attending === true) {
      if (!plusOne.name.trim()) return "Please add your guest's name.";
      if (!plusOne.meal) return "Please choose an entrée for your guest.";
    }

    // Loose on purpose — a bounced confirmation is recoverable, a guest
    // blocked from replying by a strict regex is not.
    if (!email.trim() || !email.includes("@")) {
      return "Please add an email we can confirm to.";
    }

    return "";
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    setError("");
    setPending(true);

    // A plus-one who was never granted, or who was left blank, is not stored.
    const plusOneToSave =
      invitation.plusOneAllowed && plusOne.attending !== null
        ? { ...plusOne, name: plusOne.name.trim() }
        : null;

    const reply = {
      responses,
      plusOne: plusOneToSave,
      events: anyoneAttending ? events : {},
      note: note.trim(),
      email: email.trim(),
    };

    try {
      await submitRsvp(invitation.id, reply);
      onSubmitted({ ...invitation, ...reply, respondedAt: new Date() });
    } catch {
      setError("We could not save that reply. Please check your connection and try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="rsvp__panel" onSubmit={handleSubmit} noValidate>
      <div>
        <p className="eyebrow">Replying for</p>
        <p className="h3" style={{ marginTop: "var(--s1)" }}>
          {invitation.displayName}
        </p>
      </div>

      {invitation.memberNames.map((name, index) => (
        <div className="guest-block" key={name}>
          <p className="guest-block__name">{name}</p>
          <AttendanceChips
            label={`Will ${name} be there?`}
            value={responses[index].attending}
            onChange={(attending) =>
              updateMember(index, { attending, meal: attending ? responses[index].meal : null })
            }
          />
          {responses[index].attending === true && (
            <label className="field">
              <span className="field__label">Entrée</span>
              <select
                className="field__select"
                value={responses[index].meal ?? ""}
                onChange={(e) => updateMember(index, { meal: e.target.value || null })}
              >
                <option value="">Choose an entrée</option>
                {MEALS.map((meal) => (
                  <option key={meal} value={meal}>
                    {meal}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      ))}

      {invitation.plusOneAllowed && (
        <div className="guest-block guest-block--plus-one">
          <p className="guest-block__name">Your guest</p>
          <p className="guest-block__role">Your invitation includes a plus one</p>
          <AttendanceChips
            label="Will your guest be there?"
            value={plusOne.attending}
            onChange={(attending) =>
              setPlusOne((prev) => ({
                ...prev,
                attending,
                meal: attending ? prev.meal : null,
              }))
            }
          />
          {plusOne.attending === true && (
            <>
              <label className="field">
                <span className="field__label">Guest's name</span>
                <input
                  className="field__input"
                  value={plusOne.name}
                  onChange={(e) => setPlusOne((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Who are you bringing?"
                  autoComplete="off"
                />
              </label>
              <label className="field">
                <span className="field__label">Entrée</span>
                <select
                  className="field__select"
                  value={plusOne.meal ?? ""}
                  onChange={(e) =>
                    setPlusOne((prev) => ({ ...prev, meal: e.target.value || null }))
                  }
                >
                  <option value="">Choose an entrée</option>
                  {MEALS.map((meal) => (
                    <option key={meal} value={meal}>
                      {meal}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
      )}

      {anyoneAttending && (
        <div className="chip-group">
          <span className="field__label">Which events will you attend?</span>
          <div className="chip-row chip-row--tight">
            {Object.keys(events).map((label) => (
              <button
                type="button"
                key={label}
                className={`chip${events[label] ? " chip--on" : ""}`}
                aria-pressed={events[label]}
                onClick={() => setEvents((prev) => ({ ...prev, [label]: !prev[label] }))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="field-pair">
        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="field__input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">Dietary needs or a song request</span>
        <textarea
          className="field__textarea"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything we should know"
          maxLength={1000}
        />
      </label>

      <div aria-live="polite">{error && <p className="form-error">Error: {error}</p>}</div>

      <div className="rsvp__submit-row">
        <button type="submit" className="btn btn--primary" disabled={pending}>
          {pending ? "Sending…" : "Send our reply"}
        </button>
        <button type="button" className="btn btn--outline" onClick={onStartOver}>
          Not you? Search again
        </button>
      </div>
    </form>
  );
}
